import React from 'react';
import { useIntl } from 'react-intl';
import Select from 'react-select';

type Option = {
  value: string;
  label: string;
};

interface Props {
  options: Option[];
  multi: boolean;
  placeholder: string;
  onChange: (value: any) => void;
  defaultValue?: Option | Option[];
  value?: any;
  meta?: any;
  styles?: any;
  isClearable?: boolean;
}

const groupStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const groupBadgeStyles: any = {
  backgroundColor: '#EBECF0',
  borderRadius: '2em',
  color: '#172B4D',
  display: 'inline-block',
  fontSize: 12,
  fontWeight: 'normal',
  lineHeight: '1',
  minWidth: 1,
  padding: '0.16666666666667em 0.5em',
  textAlign: 'center',
};

/**
 * Custom select input component for react-select library with multi select option and custom styles support
 *  @param {Option[]} options - options for the select input
 *  @param {boolean} multi - multi select option
 *  @param {string} placeholder - placeholder for the select input
 *  @param {(value: any) => void} onChange - onChange event handler for the select input
 *  @param {Option | Option[]} defaultValue - default value for the select input
 * @param {Props} props
 * @return {*}  {JSX.Element}
 */

const CustomSelectInput = (props: Props): JSX.Element => {
  const intl = useIntl();
  const customStyles: any = {
    container: (provided: any) => ({
      ...provided,
      width: '100%',
      whiteSpace: 'nowrap',
    }),
    control: (base: any, state: any) => ({
      ...base,
      minHeight: 36,
      height: 36,
      borderColor: state.isFocused ? '#94a3b8' : '#d1d5db',
      boxShadow: 'none',
      ':hover': {
        borderColor: '#94a3b8',
      },
    }),
    valueContainer: (base: any) => ({
      ...base,
      padding: '2px 8px',
      overflow: 'hidden',
      flexWrap: 'nowrap',
    }),
    input: (base: any) => ({
      ...base,
      margin: 0,
      padding: 0,
      minWidth: 0,
    }),
    singleValue: (base: any) => ({
      ...base,
      maxWidth: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }),
    indicatorsContainer: (base: any) => ({
      ...base,
      padding: '0 6px',
    }),
    clearIndicator: (base: any) => ({ ...base, padding: 2 }),
    dropdownIndicator: (base: any) => ({ ...base, padding: 4 }),
    indicatorSeparator: () => ({ display: 'none' }),
    placeholder: (base: any) => ({ ...base, color: '#94a3b8' }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isSelected ? '#DBEAFE' : state.isFocused ? '#f1f5f9' : 'white',
      color: '#0f172a',
      fontWeight: state.isSelected ? 600 : 400,
      ':active': { backgroundColor: state.isSelected ? '#BFDBFE' : '#e2e8f0' },
    }),
    menu: ({ width, ...css }: any) => ({ ...css, width: '100%', minWidth: 200, zIndex: 9999 }),
    menuPortal: (base: any) => ({ ...base, zIndex: 2000 }),
  };

  const formatOptionLabel = (option: any, meta: any) => {
    if (meta.context === 'value') return <span>{option.label}</span>;
    const selected = meta?.selectValue?.some((v: any) => v?.value === option?.value);
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>{option.label}</span>
        {selected ? <span style={{ color: '#2563eb' }}>✓</span> : null}
      </div>
    );
  };

  if (props?.styles) {
    Object.keys(props.styles).forEach((key: string) => {
      const provided = (props.styles as any)[key];
      const existing = (customStyles as any)[key];
      if (typeof existing === 'function') {
        (customStyles as any)[key] = (base: any, state: any) => ({
          ...(existing ? existing(base, state) : base),
          ...(typeof provided === 'function' ? provided(base, state) : provided),
        });
      } else {
        (customStyles as any)[key] = {
          ...(existing || {}),
          ...(typeof provided === 'function' ? provided({}) : provided),
        };
      }
    });
  }

  const formatGroupLabel = (data: any) => (
    <div style={groupStyles}>
      <span>{data.label}</span>
      <span style={groupBadgeStyles}>{data.options.length}</span>
    </div>
  );

  const multiValueContainer: any = ({ selectProps, data }: any) => {
    const allSelected = Array.isArray(selectProps?.value)
      ? selectProps?.value
      : [selectProps?.value].filter(Boolean);
    const idx = allSelected.findIndex((s: any) => s?.label === data?.label);
    if (allSelected.length <= 1) {
      return data?.label ?? data?.value;
    }
    // Multiple selections: show a single compact summary on the first chip
    if (idx === 0) {
      return intl.formatMessage(
        { id: 'selectedCount', defaultMessage: '{count} selected' },
        { count: allSelected.length }
      );
    }
    // Hide subsequent chips
    return null;
  };

  return (
    <div>
      <Select
        isMulti={props.multi}
        defaultValue={props?.defaultValue}
        value={props?.value}
        components={{ MultiValueContainer: multiValueContainer }}
        options={props.options}
        formatGroupLabel={formatGroupLabel}
        formatOptionLabel={formatOptionLabel}
        closeMenuOnSelect={false}
        onChange={(value: any) => props.onChange(value)}
        hideSelectedOptions={false}
        styles={customStyles}
        menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
        menuPosition="fixed"
        isSearchable={true}
        placeholder={props.placeholder}
        isClearable={props.isClearable}
        onBlur={(e: any) => e.preventDefault()}
        blurInputOnSelect={true}
      />
    </div>
  );
};

export default CustomSelectInput;
